<script setup lang="ts">
import { TirPmTableTr, TirPmTableTd } from 'tir-pm-table';
import { computed, PropType } from 'vue';
import PerformerTaskDaysResponseItemsModel from '../../models/PerformerTaskDaysResponseItemsModel';
import {
  PopoverColorEnum,
  PopoverPositionEnum,
  TirPmIconView,
  TirPmPopover,
  TirPmTooltipElement,
} from 'tir-components';
import { RouterLink, useRouter } from 'vue-router';
import DailySummariesModel from '../../models/DailySummariesModel';

const props = defineProps({
  /**Фильтр мониторинга */
  filter: { type: Object as PropType<MonitoringHeaderFilterModel> },
  /**Данные юзера */
  dataUsers: { type: Array as PropType<PerformerTaskDaysResponseItemsModel[]> },
  /**Массив с ширинами колонок */
  columnWidths: { type: Array as PropType<string[]>, default: () => [] },
  /**Ежедневные сводки */
  dailySummaries: { type: Array as PropType<DailySummariesModel[]> },
});

const emit = defineEmits<{
  /**Событие открытия шторки с задачами */
  (e: 'EventOpenTasksListDrawer', user: PerformerTaskDaysResponseItemsModel, daily: any);
}>();

/**Роутер */
const router = useRouter();

/**Перейти к задачам пользователя */
const routeToUserTasks = (userId: number) => {
  return router.resolve({
    name: 'user-tasks',
    params: { userId },
  }).href;
};

/** Получить общее время */
const getTotalTime = (time: DailySummariesModel[]) => {
  let hours = time.reduce((acc, t) => acc + parseInt(String(t.TotalTimeSpent)), 0);
  let totalMinutes = time.reduce((acc, t) => acc + (t.TotalTimeSpent % 1), 0) * 100;

  if (totalMinutes > 60) {
    hours += totalMinutes / 60;
    totalMinutes = totalMinutes % 60;
  }

  return `${hours.toFixed(0).padStart(2, '0')}:${totalMinutes.toFixed(0).padStart(2, '0')}`;
};

/**Отфильтрованные колонки */
const filteredDailySummaries = computed(() => {
  const uniqueDates = new Set();

  return props.dailySummaries?.filter((item) => {
    const formattedDate = new Date(item.Date).toISOString().split('T')[0];

    if (uniqueDates.has(formattedDate)) {
      return false;
    }

    uniqueDates.add(formattedDate);
    return true;
  });
});

/**Данные о дне */
const computedDayData = computed(() => {
  const sortedData = filteredDailySummaries.value
    .map((daily) => {
      const dayData = getDayWeek(daily.Date);
      return {
        ...daily,
        isWeekend: dayData.isWeekend,
        day: dayData.day,
      };
    })
    .sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());

  return sortedData;
});

/**Получить день недели */
const getDayWeek = (date: Date) => {
  let newDate = new Date(date);
  newDate.setDate(newDate.getDate());

  const dayOfWeek = newDate.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  return {
    day: newDate.toLocaleDateString('ru-RU', { weekday: 'short' }).toUpperCase(),
    isWeekend,
  };
};

/**Открытие шторки с задачами */
const openTasksListDrawer = (user: PerformerTaskDaysResponseItemsModel, daily: any) => {
  emit('EventOpenTasksListDrawer', user, daily);
};

/**Форматирование времени */
const formatTime = (time: number, finalTime?: boolean) => {
  const workTime = time.toFixed(2);

  const hours = workTime.split('.')[0].padStart(2, '0');
  const minutes = workTime.split('.')[1].padStart(2, '0');

  if (finalTime) {
    return workTime.replace('.', 'ч ') + 'м';
  }
  return `${hours}:${minutes}`;
};

/**Получает отформатированное время, затраченное пользователем за указанный день */
const getFormattedTime = (user, daily) => {
  const timeSpent = user.DailySummaries.find(
    (d) =>
      new Date(d.Date).toISOString().split('T')[0] ===
      new Date(daily.Date).toISOString().split('T')[0],
  )?.TotalTimeSpent;
  return timeSpent ? formatTime(timeSpent) : '';
};
</script>

<template>
  <TirPmTableTr
    v-for="user in dataUsers"
    class="monitoring-days-table-info__rows"
    :columns="columnWidths"
  >
    <TirPmTableTd :isLeftBorder="false" class="monitoring-days-table-info__data">
      <TirPmIconView
        :size="24"
        isRounded
        :isBordered="false"
        :colorCode="user.UsersData.AvatarColor"
        :imageLoadUrl="user.UsersData.Foto"
      >
        <template #mainSlot>
          {{ user.UsersData.LastName[0] }}{{ user.UsersData.FirstName[0] }}
        </template>
      </TirPmIconView>
      <TirPmTooltipElement class="monitoring-days-table-info__data-user">
        <template #default>
          <RouterLink
            class="monitoring-days-table-info__data-user"
            :to="routeToUserTasks(user.UsersData.Id)"
          >
            {{ user.UsersData.LastName }} {{ user.UsersData.FirstName }}
            {{ user.UsersData.MiddleName }}
          </RouterLink>
        </template>
        <template #content>
          <div class="monitoring-days-table-info__user-info">
            {{ user.UsersData.LastName }} {{ user.UsersData.FirstName }}
            {{ user.UsersData.MiddleName }}
          </div>
        </template>
      </TirPmTooltipElement>
    </TirPmTableTd>
    <TirPmTableTd class="monitoring-days-table-info__data-general-hours">
      {{ getTotalTime(user.DailySummaries) }}
    </TirPmTableTd>
    <TirPmTableTd
      v-for="daily in computedDayData"
      class="monitoring-days-table-info__data-day-work-block"
      :class="{ 'weekend-day': daily.isWeekend }"
    >
      <div
        @click.stop="openTasksListDrawer(user, daily)"
        class="monitoring-days-table-info__data-day-work"
      >
        {{ getFormattedTime(user, daily) }}
      </div>
    </TirPmTableTd>
  </TirPmTableTr>
</template>

<style scoped lang="scss">
.monitoring-days-table-info {
  &__rows {
    padding: 0 0.75rem 0 0 !important;
    gap: 0 !important;
  }
  &__user-info {
    display: flex;
    justify-content: center;
    overflow-wrap: break-word;
  }
  &__data {
    position: sticky !important;
    left: 0;
    z-index: 1;
    padding-left: 0.75rem !important;
    background-color: var(--neutral-05);
  }

  &__data-user {
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.875rem;
    font-weight: 400;
    color: var(--text-primary, #272d37);
    cursor: pointer;
    &:hover {
      font-weight: 500;
      color: var(--text-info, #2a77ef);
    }
  }

  &__data-general-hours {
    position: sticky !important;
    left: 14.438rem;
    z-index: 1;
    padding-left: 0.75rem !important;
    background-color: var(--neutral-05);
    width: 100%;
    border-right: 1px solid var(--neutral-20, #d4d5d7);
  }

  &__data-day-work-block {
    padding: 0 !important;
    font-weight: 400;
    &::before {
      background-color: var(--neutral-05) !important;
      height: 100%;
      margin-top: -4px;
    }
  }

  &__data-day-work {
    margin-left: 0.75rem;
    cursor: pointer;
    &:hover {
      font-weight: 500;
      color: var(--text-info, #2a77ef);
    }
  }
}
</style>

<style lang="scss">
.table-grid-tr.monitoring-days-table-info__rows:hover {
  .table-grid-td.monitoring-days-table-info__data.table-grid-td,
  .monitoring-days-table-info__data-general-hours {
    background: var(--neutral-20, #d4d5d7);
    font-weight: 400;
  }
}
.monitoring-days-table-info__data-general-hours {
  &::before {
    height: 100%;
    margin-top: -4px;
  }
}
</style>
